// Org risk trend chart.
//
// Data: real daily org-score history from the risk_trend aggregation
// (tenancy_risk_org_daily, written by the refresh worker; historical gap
// fillable with reportsb-refresh/backfill.py). No synthetic fallback -
// with under 3 days of history the card shows a collecting-history note,
// never an invented curve.
import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MainCard from '../../../../components/cards/MainCard';
import { motion, AnimatePresence } from 'motion/react';
import useDashboardData from '../../hooks/useDashboardData';
import TrendAreaChart, { RED_FADE } from '../TrendAreaChart';

const ORG_COLOR = '#FF5252';

// The two views, keyed by the value stored in localStorage. Simple is an
// edge-to-edge sparkline; detailed is padded, with axes and the Org key.
const VIEWS = {
    detailed: {
        series: [{ key: 'org', name: 'Org', color: ORG_COLOR, width: 3, stops: RED_FADE }],
        sparkline: false,
        headerPr: 'calc(24px + 18px)',
        chartPad: 3,
    },
    simple: {
        series: [{ key: 'org', name: 'Org', color: ORG_COLOR, stops: [[0, '#FF6B7A', 0.28], [100, ORG_COLOR, 0]] }],
        sparkline: true,
        headerPr: 3,
        chartPad: 0,
    },
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// UTC throughout: the rollup is keyed by UTC day.
const fmtDay = (ms) => { const d = new Date(ms); return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`; };
const fmtFullDay = (ms) => `${fmtDay(ms)} ${new Date(ms).getUTCFullYear()}`;

// Numeric time axis, so a missing day leaves a gap instead of squeezing up.
const TIME_AXIS = { type: 'number', scale: 'time', domain: ['dataMin', 'dataMax'], tickFormatter: fmtDay };
const TOOLTIP = { labelFormatter: fmtFullDay };

export const useRiskTrendQuery = (scope, opts) => useDashboardData({ metric: 'simulations', aggregation: 'risk_trend', scope }, opts);

export default function RiskTrendChart({ scope }) {
    const theme = useTheme();
    const [viewKey, setViewKey] = useState(() => (localStorage.getItem('rpt_risktrend_view') === 'simple' ? 'simple' : 'detailed'));
    const view = VIEWS[viewKey];
    // Real daily org-score history from the rollup. Under 3 days of history the
    // card shows a collecting-history note instead of a chart.
    const trendQ = useRiskTrendQuery(scope);
    const aggTrend = trendQ.data?.riskTrend;
    const hasTrend = Array.isArray(aggTrend) && aggTrend.length >= 3;
    const trend = hasTrend ? aggTrend.map((p) => ({ ms: Date.parse(`${p.day}T00:00:00Z`), org: p.score })) : [];

    const scores = trend.map((p) => p.org).filter((v) => v != null);
    const yAxis = { domain: [Math.max(0, Math.min(...scores) - 10), Math.min(100, Math.max(...scores) + 10)] };
    // Honest span label from the actual data range, e.g. "22 Jul - 12 Aug".
    const subtitle = hasTrend ? `${fmtDay(trend[0].ms)} - ${fmtDay(trend[trend.length - 1].ms)} - your org score` : 'Daily org score history';

    const toggle = () => {
        const next = view.sparkline ? 'detailed' : 'simple';
        localStorage.setItem('rpt_risktrend_view', next);
        setViewKey(next);
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: 'easeOut' }}
            style={{ height: '100%' }}
        >
            <MainCard content={false} sx={{ bgcolor: 'background.paper', height: '100%', minHeight: 260, overflow: 'hidden' }} border={false} boxShadow={true}>
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 260 }}>
                    {/* Header, always padded, always visible */}
                    <Box sx={{ px: 3, pt: 3, pb: 1.25, flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', pr: view.headerPr }}>
                        <Box>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary', mb: '2px' }}>
                                Risk score trend
                            </Typography>
                            <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>
                                {subtitle}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            {!view.sparkline && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 10, color: 'text.secondary' }}>
                                    <Box sx={{ width: 20, height: 2, bgcolor: ORG_COLOR, borderRadius: '1px', flexShrink: 0 }} />
                                    Org
                                </Box>
                            )}
                            <ToggleButton simple={view.sparkline} isDark={theme.palette.mode === 'dark'} onToggle={toggle} />
                        </Box>
                    </Box>

                    {/* Chart area, padded in detailed view, edge-to-edge in simple */}
                    {!hasTrend ? (
                        <Box sx={{ flex: 1, minHeight: 180, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5, px: 3, pb: 3, textAlign: 'center' }}>
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>
                                Collecting daily history
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: 'text.disabled', maxWidth: 320 }}>
                                Your organisation score is recorded once a day. The trend appears after 3 days of scored activity.
                            </Typography>
                        </Box>
                    ) : (
                    <Box sx={{ flex: 1, minHeight: 180, px: view.chartPad, pb: view.chartPad }}>
                        <Box sx={{ position: 'relative', height: '100%' }}>
                            <AnimatePresence mode="sync" initial={false}>
                                <motion.div
                                    key={viewKey}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.18 }}
                                    style={{ position: 'absolute', inset: 0 }}
                                >
                                    <TrendAreaChart
                                        data={trend}
                                        xKey="ms"
                                        series={view.series}
                                        xAxis={TIME_AXIS}
                                        yAxis={yAxis}
                                        tooltip={TOOLTIP}
                                        sparkline={view.sparkline}
                                    />
                                </motion.div>
                            </AnimatePresence>
                        </Box>
                    </Box>
                    )}
                </Box>
            </MainCard>
        </motion.div>
    );
}

function ToggleButton({ simple, isDark, onToggle }) {
    const mutedColor = isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)';
    const strongColor = isDark ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.85)';
    const borderColor = isDark ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.14)';
    const bgColor = isDark ? '#333537' : '#ffffff';

    return (
        <Box
            component="button"
            onClick={onToggle}
            sx={{
                display: 'flex', alignItems: 'center', gap: '5px',
                px: '8px', py: '3px',
                border: `1px solid ${borderColor}`,
                borderRadius: '5px',
                background: bgColor,
                cursor: 'pointer',
                color: mutedColor,
                fontSize: 10,
                fontFamily: 'Inter, sans-serif',
                transition: 'border-color 150ms, color 150ms',
                '&:hover': { borderColor: mutedColor, color: strongColor },
            }}
        >
            {simple ? (
                <>
                    <Box component="span" sx={{ display: 'flex', gap: '2px', alignItems: 'flex-end', height: 10 }}>
                        {[4,7,5,9,6,8,7,10].map((h, i) => (
                            <Box key={i} sx={{ width: 2, height: h, bgcolor: 'currentColor', borderRadius: '1px' }} />
                        ))}
                    </Box>
                    Detail
                </>
            ) : (
                <>
                    <Box component="span" sx={{ width: 16, height: 10, position: 'relative', display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ position: 'absolute', width: '100%', height: '2px', bgcolor: 'currentColor', borderRadius: '1px', top: '30%' }} />
                        <Box sx={{ position: 'absolute', width: '100%', height: '2px', bgcolor: 'currentColor', borderRadius: '1px', opacity: 0.4, top: '70%' }} />
                    </Box>
                    Simple
                </>
            )}
        </Box>
    );
}
